import { Emblem } from "@/components/brand/Emblem";
import { ApiError } from "@/services/api";

import type { CheckResult } from "./types";
import { useHealth } from "./useHealth";
import { useSocketPing, type SocketStatus } from "./useSocketPing";

type Tone = "ok" | "warn" | "bad" | "idle";

const toneStyles: Record<Tone, { dot: string; text: string }> = {
  ok: { dot: "bg-primary-container shadow-glow-primary", text: "text-primary" },
  warn: { dot: "bg-secondary-container", text: "text-secondary" },
  bad: { dot: "bg-error-container", text: "text-error" },
  idle: { dot: "bg-outline animate-pulse", text: "text-on-surface-variant" },
};

function checkTone(result: CheckResult | undefined): Tone {
  if (result === "ok") return "ok";
  if (result === "disabled") return "warn";
  if (result === "error") return "bad";
  return "idle";
}

function socketTone(status: SocketStatus): Tone {
  if (status === "open") return "ok";
  if (status === "closed") return "bad";
  return "idle";
}

function StatusRow({ label, value, tone, hint }: { label: string; value: string; tone: Tone; hint?: string }) {
  const style = toneStyles[tone];
  return (
    <li className="flex items-center justify-between gap-4 py-3">
      <div>
        <p className="text-body-md text-on-surface">{label}</p>
        {hint ? <p className="text-body-sm text-on-surface-variant">{hint}</p> : null}
      </div>
      <span className={`flex items-center gap-2 text-label-md uppercase ${style.text}`}>
        <span className={`size-2 rounded-full ${style.dot}`} aria-hidden />
        {value}
      </span>
    </li>
  );
}

/**
 * M0 diagnostic screen. Replaced by the login/landing flow in M1, but kept
 * reachable later as an admin/debug page.
 */
export function SystemStatusPage() {
  const health = useHealth();
  const socket = useSocketPing();

  const apiReachable = health.isSuccess;
  const apiError = health.error instanceof ApiError ? health.error : null;
  const checks = health.data?.checks;
  const realtime = health.data?.realtime;

  const allGreen =
    health.data?.status === "ok" && socket.status === "open" && socket.rttMs !== null;

  return (
    <main className="relative flex min-h-full items-center justify-center overflow-hidden px-4 py-10">
      {/* Ambient emerald / gold glow standing in for the 3D city behind the HUD */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 size-[640px] -translate-x-1/2 rounded-full bg-primary-container/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 -right-24 size-[480px] rounded-full bg-secondary-container/10 blur-3xl"
      />

      <section className="glass-panel relative w-full max-w-lg p-6 sm:p-8">
        <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-primary-container to-transparent" />

        <header className="flex items-center gap-4">
          <Emblem className="size-12 shrink-0" />
          <div>
            <h1 className="text-headline-sm font-extrabold uppercase tracking-tight">Coal City Life</h1>
            <p className="text-label-sm uppercase text-primary">System check · Milestone 0</p>
          </div>
        </header>

        <div
          className={`mt-6 rounded-xl border px-4 py-3 text-body-md ${
            allGreen
              ? "border-primary-container/40 bg-primary-container/10 text-primary"
              : "border-outline-variant bg-surface-container-low text-on-surface-variant"
          }`}
        >
          {allGreen
            ? "All systems online. The foundation is ready for Milestone 1."
            : "Checking the city's systems…"}
        </div>

        <ul className="mt-4 divide-y divide-outline-variant/60">
          <StatusRow
            label="Django API"
            value={apiReachable ? "online" : apiError?.isNetworkError ? "unreachable" : health.isError ? "error" : "checking"}
            tone={apiReachable ? "ok" : health.isError ? "bad" : "idle"}
            hint={apiError ? apiError.message : "/api/health/"}
          />
          <StatusRow label="PostgreSQL" value={checks?.database ?? "—"} tone={checkTone(checks?.database)} />
          <StatusRow
            label="Redis"
            value={checks?.redis ?? "—"}
            tone={checkTone(checks?.redis)}
            hint={checks?.redis === "disabled" ? "USE_REDIS=False — single-process fallback" : undefined}
          />
          <StatusRow
            label="Channel layer"
            value={realtime?.channel_layer ?? "—"}
            tone={realtime ? (realtime.channel_layer === "RedisChannelLayer" ? "ok" : "warn") : "idle"}
          />
          <StatusRow
            label="Celery"
            value={realtime?.celery ?? "—"}
            tone={realtime ? (realtime.celery === "broker" ? "ok" : "warn") : "idle"}
            hint={realtime?.celery === "eager" ? "Tasks run inline (no worker needed)" : undefined}
          />
          <StatusRow
            label="WebSocket"
            value={
              socket.status === "open"
                ? socket.rttMs !== null
                  ? `${socket.rttMs} ms`
                  : "open"
                : socket.status === "closed"
                  ? `closed${socket.closeCode ? ` (${socket.closeCode})` : ""}`
                  : "connecting"
            }
            tone={socketTone(socket.status)}
            hint="/ws/ping/ via channel layer"
          />
        </ul>

        {socket.status === "closed" ? (
          <button
            type="button"
            onClick={socket.reconnect}
            className="mt-6 w-full rounded-xl bg-primary-container py-3 text-label-lg uppercase text-on-primary shadow-glow-primary transition hover:bg-primary"
          >
            Reconnect socket
          </button>
        ) : null}
      </section>
    </main>
  );
}